import { CurrencyInterface } from '@com.amway.commerce/webshop-currency-v1-prod/Schema';
import { Config } from '@com.amway.commerce/webshop-configs-v1-prod/Schema';
import { CountryInterface } from '@com.amway.commerce/masterdata-country-v1-draft/Schema';
import { ConfigService, type CurrencyFormatConfig, MasterDataService } from '@nextgen-web-framework/core';
import { BannerComponentType, CMSBannerType, PageCategory, CMSTransformerDataType, TENANT } from '../../types/common';
import getServerUrl from '../../utils/getServerUrl';
import getMasterData from './getMasterdata';
import { getLocale } from 'i18n/routingUtil';
import Configs from 'constants/configs';

type TranslationFunction = (str: string, values?: Record<string, string>) => string;

function dragElement(elmnt: HTMLElement, onClick?: () => void) {
  let currentPosLeft = 0,
    currentPosTop = 0;
  let click = false;
  elmnt.addEventListener('touchmove', dragTouchMove, { passive: false });
  elmnt.onmousedown = dragMouseDown;

  function dragTouchMove(e: TouchEvent) {
    e.preventDefault();
    const [touchLocation] = Array.from(e.targetTouches);
    elmnt.style.left = `${touchLocation.clientX - elmnt.clientWidth / 2}px`;
    elmnt.style.top = `${touchLocation.clientY - elmnt.clientWidth / 2}px`;
    elmnt.style.bottom = 'unset';
  }

  function dragMouseDown(e: MouseEvent) {
    e.preventDefault();
    // get the mouse cursor position at startup :
    currentPosLeft = e.clientX;
    currentPosTop = e.clientY;
    click = true;
    document.onmouseup = closeDragElement;
    // call a function whenever the cursor moves:
    document.onmousemove = elementDrag;
  }

  function elementDrag(e: MouseEvent) {
    e.preventDefault();
    //calculate the new cursor position:
    const newPosLeft = currentPosLeft - e.clientX;
    const newPosTop = currentPosTop - e.clientY;
    currentPosLeft = e.clientX;
    currentPosTop = e.clientY;
    // set the element's new position:
    elmnt.style.top = `${elmnt.offsetTop - newPosTop}px`;
    elmnt.style.left = `${elmnt.offsetLeft - newPosLeft}px`;
    elmnt.style.bottom = 'unset';
    click = false;
  }

  function closeDragElement() {
    if (click) {
      onClick?.();
    }
    /* stop moving when mouse button is released:*/
    document.onmouseup = null;
    document.onmousemove = null;
  }
}

const getCurrencyConfig = (currency: string, locale: string): CurrencyFormatConfig | undefined => {
  let currencyFormatConfig: CurrencyFormatConfig | undefined;
  const currencyConfigData = getMasterData(
    '@com.amway.commerce/webshop-currency-v1-prod',
    locale,
  ) as unknown as CurrencyInterface[];
  const currencyData = currencyConfigData?.find((c) => c.pk === currency);
  if (currencyData) {
    const localeCurrencyData = currencyData.locales.find((l) => l.locale === locale);
    currencyFormatConfig =
      localeCurrencyData &&
      ({
        [currency]: localeCurrencyData,
      } as unknown as CurrencyFormatConfig);
  }
  return currencyFormatConfig;
};

const getHomeBreadcrumb = (t: TranslationFunction) => ({
  name: t('web.bottomTabNavigation.home'),
  link: '/',
});

const getInvalidateTime = (PageCategoryType: string) => {
  switch (PageCategoryType) {
    case PageCategory.DEFERRED:
      return Configs.DEFERRED_REVALIDATION_INTERVAL;
    case PageCategory.STANDARD:
      return Configs.STANDARD_REVALIDATION_INTERVAL;
    default:
      return Configs.EXPRESS_REVALIDATION_INTERVAL;
  }
};

const getRedirectsRefreshInterval = () => Configs.DEFERRED_REVALIDATION_INTERVAL * 1000;

const getBannerComponentType = (bannerType: string): string => {
  let componentType = '';
  switch (bannerType) {
    case CMSBannerType.BANNER_WITH_CTA:
      componentType = BannerComponentType.HERO_BANNER;
      break;
    case CMSBannerType.VIDEO_BANNER_WITH_CTA:
      componentType = BannerComponentType.VIDEO_BANNER;
      break;
    case CMSBannerType.BANNER_WITHOUT_CTA:
      componentType = BannerComponentType.STANDARD_BANNER_WITH_NO_CTA;
      break;
    default:
      break;
  }
  return componentType;
};

const getRequestOptions = (locale?: string, serviceKey?: string) => {
  const mappedLocale = getLocale(locale as string);
  return {
    ...(typeof window === 'undefined' && serviceKey && { baseURL: getServerUrl(serviceKey) }),
    ...(mappedLocale && {
      headers: {
        ['Accept-Language']: mappedLocale,
      },
    }),
  };
};

const getSignUpUrl = () => {
  const configs = ConfigService.getConfigs();
  const redirectUrl = configs.NEXT_PUBLIC_REGISTER_REDIRECT_URL ?? '/complete-your-profile';
  return `/signup?flowName=${configs.NEXT_PUBLIC_REGISTER_FLOW}&returnUri=${redirectUrl}`;
};

const getDatesByYear = (yearStr: string) => {
  const year = parseInt(yearStr, 10);
  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  };
};

const isFeatureUnavailable = (unsupportedTenants?: string[]) => {
  const configs = ConfigService.getConfigs();
  const tenant = configs.NEXT_PUBLIC_TENANT_NAME as TENANT;
  return unsupportedTenants?.includes(tenant);
};

const getWebShopMasterData = () => {
  const webShopMasterData = MasterDataService.getMasterData('WebshopConfigs') as Config;
  if (webShopMasterData) {
    return webShopMasterData;
  }
  return getMasterData('@com.amway.commerce/webshop-configs-v1-prod') as unknown as Config;
};

const getCountryMasterData = () => {
  const countryMasterData = MasterDataService.getMasterData('MasterCountryList') as CountryInterface[];
  if (countryMasterData) {
    return countryMasterData;
  }
  return getMasterData('@com.amway.commerce/masterdata-country-v1-draft') as CountryInterface[];
};

const getConfigsData = (countryCode: string) => {
  const webShopMasterData = getWebShopMasterData();
  const countryMasterData = getCountryMasterData();
  const countryData = countryMasterData.find((countryData) => countryData.pk.toLowerCase() === countryCode);

  return {
    country: countryCode,
    tenant: countryData?.affiliateCode,
    currency: webShopMasterData.currency,
    timezone: webShopMasterData.timezone,
    tealiumProfile: webShopMasterData.tealiumProfile,
  };
};

const getAllConfigs = (countryCode: string) => {
  const { tenant, timezone, tealiumProfile, currency } = getConfigsData(countryCode);
  return {
    NEXT_PUBLIC_TENANT: tenant,
    NEXT_PUBLIC_TIMEZONE: timezone,
    NEXT_PUBLIC_TEALIUM_PROFILE: tealiumProfile,
    NEXT_PUBLIC_CURRENCY: currency,
    NEXT_PUBLIC_REQUEST_TIMEOUT: Configs.REQUEST_TIMEOUT,
    NEXT_PUBLIC_TEALIUM_ACCOUNT: Configs.TEALIUM_ACCOUNT,
    NEXT_FALLBACK_LANGUAGE: Configs.FALLBACK_LANGUAGE,
    NEXT_BONUS_HIGHEST_LEVEL: Configs.BONUS_HIGHEST_LEVEL,
    NEXT_COOKIE_POLICY_URL: Configs.COOKIE_POLICY_URL,
    channel: Configs.CHANNEL,
    scriptSource: Configs.ADA_SCRIPT_SOURCE,
    NEXT_PUBLIC_CONTENT_STACK_CDN_URL: Configs.CONTENT_STACK_CDN_URL,
  };
};

const getActiveTabTitle = (header: CMSTransformerDataType, activeTabUrl: string, isAnonymous: boolean) => {
  const menuHeader = (Array.isArray(header) ? header[0] : header) ?? {};
  const mobileMenuItem = menuHeader?.mobile_bottom_navigation_menu?.[0]?.menu_items?.find(
    (item: CMSTransformerDataType) => item.menu_url === activeTabUrl,
  );
  if (mobileMenuItem) {
    return isAnonymous ? mobileMenuItem.title_guest : mobileMenuItem.title_logged;
  }
  return null;
};

export {
  dragElement,
  getBannerComponentType,
  getCurrencyConfig,
  getHomeBreadcrumb,
  getInvalidateTime,
  getRedirectsRefreshInterval,
  getRequestOptions,
  getSignUpUrl,
  getDatesByYear,
  isFeatureUnavailable,
  getWebShopMasterData,
  getCountryMasterData,
  getConfigsData,
  getActiveTabTitle,
  getAllConfigs,
};
